import { Localized } from "@fluent/react";
import CloseIcon from "@mui/icons-material/Close";
import { DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { CancelButton } from "../../../app/components/CancelButton";
import { SaveButton } from "../../../app/components/SaveButton";
import { useAppDispatch } from "../../../app/hooks";
import { closeSupplierCollaboration } from "../suppliersSlice";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { AppId } from "../../../app/appTypes";

interface CloseCollaborationDialogProps {
    supplierId: AppId;
    onClose: () => void;
    onSuccess: () => void;
}

export const CloseCollaborationDialog = ({ supplierId, onClose, onSuccess }: CloseCollaborationDialogProps) => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);

    const handleConfirm = useCallback(async () => {
        try {
            setLoading(true);
            const result = await dispatch(closeSupplierCollaboration(supplierId));
            if (closeSupplierCollaboration.fulfilled.match(result)) {
                dispatch(showSuccess("saved"));
                onSuccess();
            } else {
                dispatch(showError("error"));
            }
        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setLoading(false);
        }
    }, [supplierId, dispatch, onSuccess]);

    return (
        <>
            <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Localized id="supplier-close-collaboration">_Close Collaboration</Localized>
                <IconButton
                    onClick={onClose}
                    disabled={loading}
                    size="small"
                    sx={{ position: "absolute", right: 12, top: 12 }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ py: 3, px: 3 }}>
                <Stack spacing={2}>
                    <Typography variant="body1">
                        <Localized id="supplier-confirm-close-desc">
                            _Are you sure you want to close collaboration with this supplier?
                        </Localized>
                    </Typography>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <SaveButton 
                    loading={loading} 
                    onClick={handleConfirm}
                    label={<Localized id="supplier-close-collaboration">_Close Collaboration</Localized>}
                />
                <CancelButton loading={loading} onClick={onClose} />
            </DialogActions>
        </>
    );
};
